/* eslint-disable no-nested-ternary */
import { useEffect, useRef, useState } from "react";

import { faRotateLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { TreatmentEditorMode } from "routes/patients/views/Treatment/components/TreatmentEditor/types";
import { BaseButton } from "shared/atoms/BaseButton";
import { useWindowSize } from "utils/hooks";

interface TreatmentEditorHeaderProps {
  mode: TreatmentEditorMode;
  canUndo?: boolean;
  hasErrors?: boolean;
  isLoading?: boolean;
  onActivate?: () => void;
  onReset: () => void;
  onSave?: () => void;
  onSaveDraft?: () => void;
  onUndo?: () => void;
}

export const TreatmentEditorHeader: React.FC<TreatmentEditorHeaderProps> = ({
  mode,
  canUndo = false,
  hasErrors = false,
  isLoading,
  onActivate,
  onReset,
  onSave,
  onSaveDraft,
  onUndo,
}) => {
  const { t } = useTranslation();
  const { isLargeScreen } = useWindowSize();
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowActionsMenu(false);
      }
    };

    if (showActionsMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActionsMenu]);

  return (
    <Container>
      <RightSection>
        {onUndo && isLargeScreen && (
          <ActionsContainer>
            <StyledBaseButton
              onClick={onUndo}
              disabled={!canUndo || isLoading}
              title={t("common.undo")}
              icon="undo"
              size="large"
              variant="secondary"
              className="split-button-first"
            />
            <ActionsMenuContainer ref={menuRef}>
              <StyledBaseButton
                onClick={() => setShowActionsMenu(!showActionsMenu)}
                disabled={isLoading}
                icon="chevron-down"
                size="large"
                variant="secondary"
                className="split-button-second"
              />
              {showActionsMenu && (
                <ActionsDropdown>
                  <DropdownItem
                    onClick={() => {
                      onReset();
                      setShowActionsMenu(false);
                    }}
                    $danger
                  >
                    <FontAwesomeIcon icon={faRotateLeft} />
                    <span>{t("treatment.editor.clear_program.button")}</span>
                  </DropdownItem>
                </ActionsDropdown>
              )}
            </ActionsMenuContainer>
          </ActionsContainer>
        )}
        {!onActivate && onSave && (
          <StyledBaseButton
            text={mode === "build" ? t("treatment.editor.create_program") : t("treatment.editor.update_program")}
            icon="check"
            onClick={onSave}
            size="large"
            disabled={isLoading || hasErrors}
          />
        )}
        {onSaveDraft && isLargeScreen && (
          <StyledBaseButton
            text={t("buttons.save")}
            icon="floppy-disk"
            onClick={onSaveDraft}
            size="large"
            variant="secondary"
            disabled={isLoading}
          />
        )}
        {onActivate && (
          <StyledBaseButton
            text={t("buttons.activate")}
            icon="check"
            onClick={onActivate}
            size="large"
            disabled={isLoading || hasErrors}
          />
        )}
      </RightSection>
    </Container>
  );
};

const StyledBaseButton = styled(BaseButton)`
  flex-shrink: 1;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 24px;

  ${({ theme }) => theme.aboveBreakpoint} {
    flex-direction: row;
    gap: 4px;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${({ theme }) => theme.aboveBreakpoint} {
    justify-content: flex-end;
    margin-left: auto;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0;

  & .split-button-first {
    border-radius: 80px 0 0 80px;
    border-right: none;
    padding: 0 20px;
  }

  & .split-button-second {
    border-radius: 0 80px 80px 0;
    padding: 0 12px;
  }
`;

const ActionsMenuContainer = styled.div`
  position: relative;
`;

const ActionsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #ffffff;
  border: ${({ theme }) => theme.semantic.container.border};
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  overflow: hidden;
  z-index: 1000;
`;

const DropdownItem = styled.button<{ disabled?: boolean; $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: ${({ disabled, $danger }) => (disabled ? "#cbd5e0" : $danger ? "#EF4444" : "#1a2332")};
  font-size: 14px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: all 0.15s ease;
  text-align: left;

  svg {
    font-size: 16px;
    color: ${({ disabled, $danger }) => (disabled ? "#cbd5e0" : $danger ? "#EF4444" : "#5c6f82")};
  }

  &:hover:not(:disabled) {
    background-color: ${({ $danger }) => ($danger ? "#ffebee" : "#f8f9fa")};
  }

  &:active:not(:disabled) {
    background-color: ${({ $danger }) => ($danger ? "#ffcdd2" : "#e1e8ef")};
  }
`;
